/**
 * US state full-name ↔ 2-letter abbreviation map.
 *
 * Why this exists in the UI: the intake form persists `data.state` as the full
 * name (e.g. "South Carolina") for display, but the Account fields it gets
 * compared against — `State_EmpApp__c` and `BillingState` — are written as
 * 2-letter codes ("SC") by the backend mapping (because downstream carrier-
 * appointment picklists only accept 2-letter codes). To stop the "differs from
 * proposal" banner from showing a false mismatch, normalize both sides of the
 * state comparison to the same form before comparing.
 *
 * Codes mirror the `agentsync__State_Codes` global value set used by the
 * Carrier_Appointment__c.State__c picklist: 50 states + DC + GU + PR + VI.
 */
const STATE_NAME_BY_ABBR: Readonly<Record<string, string>> = {
  AK: "Alaska", AL: "Alabama", AR: "Arkansas", AZ: "Arizona", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", GU: "Guam", HI: "Hawaii", IA: "Iowa", ID: "Idaho",
  IL: "Illinois", IN: "Indiana", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  MA: "Massachusetts", MD: "Maryland", ME: "Maine", MI: "Michigan", MN: "Minnesota",
  MO: "Missouri", MS: "Mississippi", MT: "Montana", NC: "North Carolina",
  ND: "North Dakota", NE: "Nebraska", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NV: "Nevada", NY: "New York", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", PR: "Puerto Rico", RI: "Rhode Island",
  SC: "South Carolina", SD: "South Dakota", TN: "Tennessee", TX: "Texas",
  UT: "Utah", VA: "Virginia", VI: "U.S. Virgin Islands", VT: "Vermont",
  WA: "Washington", WI: "Wisconsin", WV: "West Virginia", WY: "Wyoming",
};

const ABBR_BY_LOWER_NAME: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(STATE_NAME_BY_ABBR).map(([abbr, name]) => [name.toLowerCase(), abbr]),
);

/**
 * Normalize a US state value to its 2-letter code when possible.
 * Accepts existing 2-letter codes, full names (any case), or blank.
 * Pass-through for anything not recognized.
 *
 * Used to compare form-stored state ("South Carolina") against Account-stored
 * state ("SC") without false mismatches.
 */
export function normalizeStateValue(value: string | null | undefined): string {
  if (!value) return "";
  const trimmed = value.trim();
  if (trimmed.length === 2 && STATE_NAME_BY_ABBR[trimmed.toUpperCase()]) {
    return trimmed.toUpperCase();
  }
  const abbr = ABBR_BY_LOWER_NAME[trimmed.toLowerCase()];
  return abbr ?? trimmed;
}
